import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import * as Icons from "lucide-react";
import ListingStatsCard from "../components/ListingStatsCard";
import PublicLinkError from "../components/PublicLinkError";
import { PUBLIC_BASE, fetchPublicWithRetry } from "../lib/api";

const BACKEND_URL = PUBLIC_BASE;

/**
 * Seller Portal — a branded, read-only view of a single listing's campaign
 * stats that the realtor shares with their seller (one link, no login).
 * Differs from the realtor dashboard in that:
 *   - Only one listing (the seller's own)
 *   - No editing, no OH flyer button, no hot-lead inbox (those belong to
 *     the realtor)
 *   - Engagement totals (views, RSVPs, cities) shown as positive proof
 *     of the marketing campaign working
 *   - Refreshes every 45s so the seller watches numbers tick up live
 */
export default function SellerPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null); // null | "transient" | "not_found"
  const [retrying, setRetrying] = useState(false);

  const loadFirst = (opts = {}) => {
    setErr(null);
    setRetrying(opts.fromUserClick === true);
    return fetchPublicWithRetry(`${BACKEND_URL}/api/public/seller/${token}`)
      .then((d) => {
        setData(d);
        setErr(null);
      })
      .catch((e) => {
        setErr(e?.kind === "not_found" ? "not_found" : "transient");
      })
      .finally(() => setRetrying(false));
  };

  useEffect(() => {
    let cancelled = false;
    let firstLoad = true;
    const load = () => {
      if (firstLoad) {
        firstLoad = false;
        if (!cancelled) loadFirst();
      } else {
        axios
          .get(`${BACKEND_URL}/api/public/seller/${token}`)
          .then((r) => !cancelled && setData(r.data))
          .catch(() => {});
      }
    };
    load();
    const t = setInterval(load, 45000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (err) {
    return (
      <PublicLinkError
        kind={err}
        retrying={retrying}
        onRetry={() => loadFirst({ fromUserClick: true })}
        testId="seller-portal-invalid"
        context="your portal"
      />
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Icons.Loader2 size={20} className="text-zinc-600 animate-spin" />
      </div>
    );
  }

  const { listing, brand, totals, reels, pins } = data;
  const accent = brand?.company_color || "#FFFFFF";

  return (
    <div
      className="min-h-screen bg-[#050505] text-white"
      style={{ "--brand": accent }}
      data-testid="seller-portal"
    >
      {brand?.company_color && (
        <div className="h-1 w-full" style={{ background: brand.company_color }} />
      )}

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-6 pt-10 pb-6">
        {listing.cover_image_url && (
          <div className="rounded-2xl overflow-hidden mb-6 aspect-[16/9] bg-black">
            <img
              src={`${BACKEND_URL}${listing.cover_image_url}`}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div
          className="text-[10px] tracking-[0.4em] uppercase mb-2"
          style={{ color: brand?.company_color || "#71717a" }}
        >
          Your campaign · Live
        </div>
        <div className="font-display text-3xl sm:text-4xl text-white">
          {listing.address}
        </div>
        <div className="text-sm text-zinc-400 mt-1">
          Marketed by {brand?.realtor_name}
          {brand?.brokerage_name ? ` · ${brand.brokerage_name}` : ""}
        </div>

        <ListingStatsCard
          stats={listing.stats}
          brand={brand?.company_color}
          variant="default"
          data-testid="seller-portal-stats"
        />
      </div>

      {/* Engagement totals — the "wow" numbers for the seller */}
      <div className="max-w-4xl mx-auto px-6 pb-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Totals label="Total views" value={totals.views} icon={<Icons.Eye size={12} />} brand={brand?.company_color} />
          <Totals label="Cities reached" value={totals.cities} icon={<Icons.Globe size={12} />} brand={brand?.company_color} />
          <Totals label="RSVPs" value={totals.rsvps} icon={<Icons.CalendarCheck size={12} />} brand={brand?.company_color} />
          <Totals label="Enquiries" value={totals.email_leads + totals.questions} icon={<Icons.MessageSquare size={12} />} brand={brand?.company_color} />
        </div>
        {totals.last_viewed && (
          <div className="text-[11px] text-zinc-500 mt-2 text-center">
            Last view {new Date(totals.last_viewed).toLocaleString()}
          </div>
        )}
      </div>

      {/* Cities */}
      {pins?.length > 0 && (
        <div className="max-w-4xl mx-auto px-6 pb-8">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">
            Where your buyers are watching from
          </div>
          <div className="flex flex-wrap gap-2">
            {pins.map((p, i) => (
              <div
                key={i}
                className="bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-sm flex items-center gap-2"
                data-testid={`seller-pin-${i}`}
              >
                <Icons.MapPin
                  size={11}
                  style={{ color: brand?.company_color || "#71717a" }}
                />
                <span className="text-white">{p.city}</span>
                {p.country_code && (
                  <span className="text-zinc-500 text-[11px]">{p.country_code}</span>
                )}
                <span
                  className="font-mono text-[11px]"
                  style={{ color: brand?.company_color || "#a1a1aa" }}
                >
                  {p.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reels */}
      <div className="max-w-4xl mx-auto px-6 pb-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">
          Your property reels
        </div>
        <div className="space-y-3">
          {reels.map((r) => (
            <a
              key={r.id}
              href={`/share/${r.share_token}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 bg-white/[0.02] border border-white/10 hover:border-white/25 rounded-xl px-4 py-3 transition-all"
              data-testid={`seller-reel-${r.id}`}
            >
              <Icons.Film size={14} className="text-zinc-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">
                  {r.title || "Property reel"}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5 flex gap-3">
                  <span>{r.view_count} views</span>
                  {r.oh_scan_count > 0 && <span>{r.oh_scan_count} OH scans</span>}
                </div>
              </div>
              <Icons.ExternalLink size={12} className="text-zinc-600 shrink-0" />
            </a>
          ))}
          {reels.length === 0 && (
            <div className="text-[12px] text-zinc-500 text-center py-6 border border-dashed border-white/10 rounded-xl">
              Reels for your listing will appear here the moment they're published.
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="max-w-4xl mx-auto px-6 py-6 border-t border-white/5 text-center">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-700 mb-1">
          Live · Refreshes every 45s
        </div>
        {brand?.realtor_email && (
          <a
            href={`mailto:${brand.realtor_email}`}
            className="text-xs text-zinc-500 hover:text-white"
          >
            Questions? Email {brand.realtor_name}
          </a>
        )}
      </div>
    </div>
  );
}

function Totals({ label, value, icon, brand }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl px-4 py-4">
      <div
        className="flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase"
        style={{ color: brand || "#71717a" }}
      >
        {icon}
        {label}
      </div>
      <div
        className="font-display text-3xl sm:text-4xl mt-1"
        style={{ color: brand || "#ffffff" }}
      >
        {value ?? 0}
      </div>
    </div>
  );
}
